import {
  ChangeDetectorRef,
  Component,
  forwardRef,
  Input,
  OnInit,
  Output,
} from '@angular/core'
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms'
import {
  BehaviorSubject,
  debounceTime,
  delay,
  distinctUntilChanged,
  map,
  Observable,
  switchMap,
  tap,
} from 'rxjs'
import {EventEmitter} from '@angular/core'

export interface OptionSelectEntry {
  label: string
  value: any
  data?: any
}

@Component({
  selector: 'option-select',
  templateUrl: './option-select.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => OptionSelectComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class OptionSelectComponent implements OnInit, ControlValueAccessor {
  @Input() loading$: BehaviorSubject<boolean> = new BehaviorSubject(false)
  @Input() options$: BehaviorSubject<OptionSelectEntry[]> = new BehaviorSubject(
    [
      {
        label: 'Option 1',
        value: 'option_1',
      },
    ],
  )
  @Input() placeholder = 'Placeholder'
  searchTextSwitchValue = 'Type to search'
  @Input() searchable = true
  @Input() showTotals = true
  @Input() delay = 0
  @Input() itemCreation = false
  @Input() itemCreationLabel = 'Item'
  @Input() spaceAsWildCard = false
  @Output() itemCreationEvent = new EventEmitter<string>()
  @Output() onValueChange = new EventEmitter()

  constructor(private cdr: ChangeDetectorRef) {}

  selectedOption: OptionSelectEntry = null
  searchTerm: string = ''
  disabled = false

  loadResultsCount: number = 0
  loadResultsTotal: number = 0
  _options$: Observable<OptionSelectEntry[]>

  typeahead = new BehaviorSubject<string>('')

  onChange = (value: any) => {}
  onTouched = () => {}
  ngOnInit(): void {
    this._options$ = this.typeahead.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      tap(() => this.loading$.next(true)),
      delay(this.delay),
      switchMap((term) => this.searchItems(term)),
      tap((results) => {
        this.loadResultsCount = results.length
        this.options$.subscribe((all) => {
          this.loadResultsTotal = all.length
        })
        this.loading$.next(false)
      }),
      delay(200),
      tap(() => {
        this.loading$.next(false)

        setTimeout(() => {
          this.cdr.detectChanges() // Trigger change detection
        })
        this.cdr.detectChanges() // Trigger change detection
      }),
    )
  }

  searchItems(term: string): Observable<OptionSelectEntry[]> {
    if (!term) {
      return this.options$
    }

    const lowerCaseTerm = term.toLowerCase()

    if (this.spaceAsWildCard) {
      const words = lowerCaseTerm
        .trim()
        .split(' ')
        .filter((word) => word.length > 0)

      return this.options$.pipe(
        map((options) => {
          const filteredOptions = options.filter((option) => {
            const optionLabel = option.label.toLowerCase()
            const matchesAllWords = words.every((word) =>
              optionLabel.includes(word),
            )
            return matchesAllWords
          })
          return filteredOptions
        }),
      )
    } else {
      return this.options$.pipe(
        map((options) =>
          options.filter((option) =>
            option.label.toLowerCase().includes(lowerCaseTerm),
          ),
        ),
      )
    }
  }

  onSearch(term: string): void {
    this.searchTerm = term
    this.typeahead.next(term)
  }

  outputEvent(): void {
    this.itemCreationEvent.emit(this.searchTerm)
  }

  searchTextFocus() {
    if (!this.searchable) {
      return
    }
    let tempText = this.placeholder
    this.placeholder = this.searchTextSwitchValue
    this.searchTextSwitchValue = tempText
  }
  searchTextBlur() {
    if (!this.searchable) {
      return
    }
    let tempText = this.placeholder
    this.placeholder = this.searchTextSwitchValue
    this.searchTextSwitchValue = tempText
  }

  writeValue(value: any): void {
    if (value) {
      if (typeof value === 'string') {
        this.selectedOption = {
          label: value,
          value: value,
        }
      } else {
        this.selectedOption = value
      }
    } else {
      this.selectedOption = null
    }
    this.onValueChange.emit(value || null)
  }

  registerOnChange(fn: any): void {
    this.onChange = fn
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled
  }

  onSelectionChange(option: OptionSelectEntry): void {
    this.selectedOption = option
    this.onChange(option)
    this.onTouched()
  }

  compareWith(item: OptionSelectEntry, selected: OptionSelectEntry): boolean {
    if (!item || !selected) {
      return false
    }
    return item.value === selected.value
  }
}
